import { ajvResolver } from '@hookform/resolvers/ajv'
import { NavigateBefore, NavigateNext } from '@mui/icons-material'
import { AxiosError } from 'axios'
import { Form } from 'components/form'
import Header from 'components/header'
import HeaderInfo from 'components/header-info'
import Page from 'components/page'
import Wrapper from 'components/wrapper'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import ThemeEditSchema, { IThemeEditFormData } from 'schema/common/theme-edit'
import api from 'services/api'
import Application from './application'
import ColorConfig from './color-config'
import LogoUpload from './logo-upload'
import Validation from './validation'

const steps: string[] = ['Upload de logos', 'Configure suas cores', 'Aplicações', 'Válide sua configuração']

const ThemeForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [activeStep, setActiveStep] = useState<number>(0)
  const { user, updateUserData } = useAuth()
  const { openAlert } = useAlert()
  const history = useHistory()

  const formData = useForm<IThemeEditFormData>({
    resolver: ajvResolver(ThemeEditSchema),
    defaultValues: {
      baseLogoFile: '',
      whiteLogoFile: '',
      blackLogoFile: '',
      baseLogo: '',
      whiteLogo: '',
      blackLogo: '',
      primaryColor: '000000',
      secondaryColor: '000000',
      terciaryColor: '000000',
    },
  })

  const { handleSubmit, watch, getValues } = formData

  const onSubmit: SubmitHandler<IThemeEditFormData> = useCallback(
    async (data) => {
      try {
        setIsLoading(true)
        const formData = new FormData()

        formData.append('baseLogo', data.baseLogoFile)
        formData.append('whiteLogo', data.whiteLogoFile)
        formData.append('blackLogo', data.blackLogoFile)
        formData.append('primaryColor', data.primaryColor)
        formData.append('secondaryColor', data.secondaryColor)
        formData.append('terciaryColor', data.terciaryColor)

        await api.post('/themes', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        await updateUserData()

        openAlert('Tema criado com sucesso', 'success')
        setIsLoading(false)
        history.push('/')
      } catch (error) {
        setIsLoading(false)
        const err = error as AxiosError
        console.log(err.response)
        openAlert('Erro ao criar tema', 'error')
      }
    },
    [history]
  )

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleNext = async () => {
    if (activeStep !== 3) {
      return setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const values = getValues()

    const data: IThemeEditFormData = {
      baseLogoFile: values.baseLogoFile,
      whiteLogoFile: values.whiteLogoFile,
      blackLogoFile: values.blackLogoFile,
      baseLogo: values.baseLogo,
      whiteLogo: values.whiteLogo,
      blackLogo: values.blackLogo,
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      terciaryColor: values.terciaryColor,
    }

    await onSubmit(data)
  }

  const validateStepButton = () => {
    if (activeStep == 0) {
      return watch('baseLogo') == null || watch('whiteLogo') == null
    }
  }

  useEffect(() => {
    if (user && (!user.isSeller || user.theme)) {
      history.push('/')
    }
  }, [user])

  return (
    <Wrapper>
      <Header>
        <HeaderInfo
          title='Personalize sua Conta'
          subTitle='Gerencie sua marca para que seus clientes possam ver a plataforma a sua identidade visual'
        />
      </Header>

      <Page>
        <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
          <Form.Stepper
            activeStep={activeStep}
            steps={steps}
          />

          <Form.Body>
            {activeStep === 0 && <LogoUpload formData={formData} />}
            {activeStep === 1 && <ColorConfig formData={formData} />}
            {activeStep === 2 && <Application formData={formData} />}
            {activeStep === 3 && <Validation formData={formData} />}
          </Form.Body>

          <Form.Divisor />

          <Form.Footer>
            <Form.Button
              label={activeStep === 3 ? 'Finalizar' : 'Continuar'}
              type={'button'}
              variant='contained'
              isLoading={isLoading}
              isDisabled={validateStepButton()}
              endIcon={<NavigateNext />}
              onClick={handleNext}
            />

            {activeStep !== 0 && (
              <Form.Button
                label='Voltar'
                type='back'
                variant='text'
                onClick={handleBack}
                startIcon={<NavigateBefore />}
              />
            )}
          </Form.Footer>
        </Form.Root>
      </Page>
    </Wrapper>
  )
}

export default ThemeForm
